import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import CircleIcon from '@mui/icons-material/Circle';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  type SwitchProps,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import React, { type ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import { DropzoneAreaBase, type FileObject } from 'react-mui-dropzone';
import { Card } from 'src/Administration/Common';
import systemSettingConfig from 'src/api/admin/systemSettingConfigs';
import { configImage, createConfig, updateConfig } from 'src/api/configurations';
import allPermissionGroup from 'src/api/permission/allPermissionGroup';
import allThemesFiltered from 'src/api/theme/allThemes';
import updateThemeMutation from 'src/api/theme/updateTheme';
import {
  ALLOW_SELF_REGISTRATION,
  CUSTOM_LOGIN_MESSAGE,
  DEFAULT_PERMISSIONS_GROUP,
  DEFAULT_SSO_PERMISSIONS_DELETE,
  DEFAULT_SSO_PERMISSIONS_DOWNLOAD,
  DEFAULT_SSO_PERMISSIONS_EDIT,
  DEFAULT_SSO_PERMISSIONS_GROUP,
  DEFAULT_SSO_PERMISSIONS_SHARE,
  DOWNLOAD_HIRES,
  DOWNLOAD_MARKERS,
  EMAIL_FROM_ADDRESS,
  ENABLE_OAUTH,
  FAV_ICON_OPTION_KEY,
  HEADER_LOGO_OPTION_KEY,
  HOME_PATH,
  LICENSE,
  LOGIN_LOGO_OPTION_KEY,
  OAUTH_CLIENT_ID,
  OAUTH_CLIENT_SECRET,
  OAUTH_PROVIDER_NAME,
  OIDC_DISCOVERY_URL,
  SITE_TITLE,
  SMTP_HOST,
  SMTP_PASSWORD,
  SMTP_PORT,
  SMTP_USER,
  THEME_PRIMARY_COLOR,
  THEME_SECONDARY_COLOR,
} from 'src/constants';
import AuthContext from 'src/context/AuthContext';
import { buildForceLicenseMutation } from 'src/dynamoQueries/license';
import { useCustomToast } from 'src/hooks/menu';
import useReindex from 'src/hooks/useReindex';
import viewStateVar from 'src/store/viewState';
import { parseThemeColors, ThemeColorGroup } from '../Common/AdminColorPicker';
import { AdminTextInput } from '../Common/input';
import ReindexCard from './ReindexCard';
import styles from './SystemSettingsPageStyles';

const normalizeThemeColor = (color: unknown): string => {
  if (!color) return JSON.stringify(parseThemeColors(''));
  const str = typeof color === 'string' ? color : JSON.stringify(color);
  return JSON.stringify(parseThemeColors(str));
};

const GRID_CONTAINER_SPACING = 4;

interface ConfigEdge {
  node: {
    id: string;
    optionValue: string;
    imageData?: string;
  };
}

interface ConfigData {
  edges: ConfigEdge[];
}

interface AllConfigsData {
  [key: string]: ConfigData;
}

interface ThemeData {
  allThemes: {
    edges: Array<{
      node: {
        primary: string;
        secondary: string;
      };
    }>;
  };
}

interface PermissionGroup {
  name: string;
}

interface PermissionGroupNode {
  node: PermissionGroup;
}

interface AllPermissionGroupsData {
  allPermissionGroups: {
    edges: PermissionGroupNode[];
  };
}

interface ConfigImageData {
  allConfigurations: {
    edges: Array<{
      node: {
        id: string;
        imageData: string;
      };
    }>;
  };
}

interface FormValues {
  [key: string]: string | number | undefined;
  theme_primary_color?: string;
  theme_secondary_color?: string;
}

interface LogoState {
  [key: string]: string;
}

interface LicenseData {
  forceLicenseCheck: {
    license: {
      isValid: boolean;
    };
  };
}

interface CustomSwitchProps extends Omit<SwitchProps, 'value' | 'onChange'> {
  value: number | string;
  onChangeSwitch: (values: Partial<FormValues>) => void;
}

export default function SystemSettingPage(): React.ReactElement | null {
  const { data: allConfigsData } = useQuery<AllConfigsData>(systemSettingConfig, {
    fetchPolicy: 'network-only',
  });
  const { data: defaultThemeData } = useQuery<ThemeData>(allThemesFiltered, {
    fetchPolicy: 'network-only',
  });

  if (!allConfigsData) return null;
  return <SystemSettings defaultThemeData={defaultThemeData} includedConfigs={allConfigsData} />;
}

interface SystemSettingsProps {
  includedConfigs: AllConfigsData;
  defaultThemeData: ThemeData | undefined;
}

function SystemSettings({ includedConfigs, defaultThemeData }: SystemSettingsProps): React.ReactElement | null {
  const context = useContext(AuthContext);
  const classes = styles();
  const navigate = useNavigate();
  const viewState = useReactiveVar(viewStateVar);
  const { toastr } = useCustomToast();
  const reindex = useReindex({
    onComplete: (total) => toastr.success(`Reindex complete — ${total} items indexed`),
    onError: (msg) => toastr.error(msg ?? 'Reindex failed'),
  });

  // Show toast for mutation rejections (e.g. "already in progress", "binary not found").
  // These return the hook to 'idle' phase with a message.
  const lastRejectionRef = useRef<string | null>(null);
  useEffect(() => {
    if (reindex.phase === 'idle' && reindex.message && reindex.message !== lastRejectionRef.current) {
      toastr.error(reindex.message);
      lastRejectionRef.current = reindex.message;
    } else if (reindex.phase !== 'idle') {
      lastRejectionRef.current = null;
    }
  }, [reindex.phase, reindex.message, toastr]);

  const goHomeHandler = (): void => {
    navigate({ to: HOME_PATH });
  };

  const [createConfigReq] = useMutation(createConfig);
  const [updateConfigReq] = useMutation(updateConfig);
  const [updateThemeReq] = useMutation(updateThemeMutation);

  const initialConfigValues: FormValues = {};
  for (const k of Object.keys(includedConfigs)) {
    initialConfigValues[k] = includedConfigs[k]?.edges[0] ? includedConfigs[k].edges[0].node.optionValue : undefined;
  }
  const primaryColor = defaultThemeData?.allThemes?.edges[0]?.node.primary;
  const secondaryColor = defaultThemeData?.allThemes?.edges[0]?.node.secondary;
  const initialThemeValues = {
    theme_primary_color: normalizeThemeColor(primaryColor),
    theme_secondary_color: normalizeThemeColor(secondaryColor),
  };

  const [themeDataLoaded, setThemeDataLoaded] = useState(!!defaultThemeData);
  const skipThemeHydrationRef = useRef(false);
  const [formInitialValues, setFormInitialValues] = useState<FormValues>(
    Object.assign({}, initialConfigValues, initialThemeValues),
  );

  const [logos, setLogos] = useState<LogoState>({
    [LOGIN_LOGO_OPTION_KEY]: '',
    [HEADER_LOGO_OPTION_KEY]: '',
    [FAV_ICON_OPTION_KEY]: '',
  });

  const { data: loginData, refetch: refetchLoginData } = useQuery<ConfigImageData>(configImage, {
    variables: { optionKey: LOGIN_LOGO_OPTION_KEY },
    fetchPolicy: 'cache-and-network',
  });
  const loginLogo = loginData?.allConfigurations?.edges?.[0]?.node;

  const { data: headerData, refetch: refetchHeaderData } = useQuery<ConfigImageData>(configImage, {
    variables: { optionKey: HEADER_LOGO_OPTION_KEY },
    fetchPolicy: 'cache-and-network',
  });
  const headerLogo = headerData?.allConfigurations?.edges?.[0]?.node;

  const { data: faviconData, refetch: refetchFaviconData } = useQuery<ConfigImageData>(configImage, {
    variables: { optionKey: FAV_ICON_OPTION_KEY },
    fetchPolicy: 'cache-and-network',
  });
  const favicon = faviconData?.allConfigurations?.edges?.[0]?.node;

  useEffect(() => {
    setLogos({
      [LOGIN_LOGO_OPTION_KEY]: loginLogo?.imageData ?? '',
      [HEADER_LOGO_OPTION_KEY]: headerLogo?.imageData ?? '',
      [FAV_ICON_OPTION_KEY]: favicon?.imageData ?? '',
    });
  }, [loginLogo, headerLogo, favicon]);

  const { data: groupsList } = useQuery<AllPermissionGroupsData>(allPermissionGroup, {
    fetchPolicy: 'cache-and-network',
  });

  const saveLogos = async (): Promise<void> => {
    const saveLogo = async (
      logoNode: { id: string } | undefined,
      optionKey: string,
      refetch: () => void,
    ): Promise<void> => {
      const imageData = logos[optionKey];
      if (!imageData) return;

      if (logoNode?.id) {
        await updateConfigReq({
          variables: {
            configurationId: logoNode.id,
            optionKey,
            image_data: imageData,
          },
        });
      } else {
        await createConfigReq({
          variables: {
            optionKey,
            image_data: imageData,
          },
        });
        refetch();
      }
    };

    await saveLogo(loginLogo, LOGIN_LOGO_OPTION_KEY, refetchLoginData);
    await saveLogo(headerLogo, HEADER_LOGO_OPTION_KEY, refetchHeaderData);
    await saveLogo(favicon, FAV_ICON_OPTION_KEY, refetchFaviconData);
  };

  const formik = useFormik<FormValues>({
    initialValues: formInitialValues,
    onSubmit: async (values: FormValues, submitProps: FormikHelpers<FormValues>) => {
      const newInitialValues = JSON.parse(JSON.stringify(values));

      const changedFileds: FormValues = {};
      for (const b of Object.keys(values)) {
        if (values[b] !== undefined && values[b] !== formInitialValues[b]) {
          changedFileds[b] = values[b];
        }
      }

      const THEME_KEYS = new Set([THEME_PRIMARY_COLOR, THEME_SECONDARY_COLOR]);
      for (const key of Object.keys(changedFileds)) {
        if (THEME_KEYS.has(key)) continue;
        const finedItem = includedConfigs[key];
        if (finedItem?.edges[0]?.node?.id) {
          updateConfigReq({
            variables: {
              configurationId: finedItem.edges[0].node.id,
              optionValue: String(changedFileds[key]),
            },
          }).catch((e: Error) => {
            toastr.error(e.message);
          });
        } else {
          createConfigReq({
            variables: {
              optionKey: key,
              optionValue: String(changedFileds[key]),
            },
          }).catch((e: Error) => {
            toastr.error(e.message);
          });
        }
      }

      const themeChanged =
        changedFileds[THEME_PRIMARY_COLOR] !== undefined || changedFileds[THEME_SECONDARY_COLOR] !== undefined;
      if (themeChanged) {
        // User has explicitly submitted theme values; do not let late query hydration overwrite them.
        skipThemeHydrationRef.current = true;
        setThemeDataLoaded(true);
        updateThemeReq({
          variables: {
            primary: values.theme_primary_color,
            secondary: values.theme_secondary_color,
          },
        }).catch((e: Error) => {
          toastr.error(e.message);
        });
      }

      try {
        await saveLogos();
        toastr.success('System setting changed successfully');
        setFormInitialValues(newInitialValues);
        submitProps.resetForm({ values: newInitialValues });
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Failed to save logos';
        toastr.error(message);
      }
    },
  });

  const { resetForm } = formik;
  const formikValuesRef = useRef(formik.values);
  formikValuesRef.current = formik.values;
  useEffect(() => {
    if (defaultThemeData && !themeDataLoaded && !skipThemeHydrationRef.current && !formik.dirty) {
      const primary = defaultThemeData.allThemes?.edges[0]?.node.primary;
      const secondary = defaultThemeData.allThemes?.edges[0]?.node.secondary;
      const normalizedPrimary = normalizeThemeColor(primary);
      const normalizedSecondary = normalizeThemeColor(secondary);
      const currentValues = formikValuesRef.current;
      const nextValues = { ...currentValues };
      if (currentValues[THEME_PRIMARY_COLOR] === formInitialValues[THEME_PRIMARY_COLOR]) {
        nextValues[THEME_PRIMARY_COLOR] = normalizedPrimary;
      }
      if (currentValues[THEME_SECONDARY_COLOR] === formInitialValues[THEME_SECONDARY_COLOR]) {
        nextValues[THEME_SECONDARY_COLOR] = normalizedSecondary;
      }
      resetForm({ values: nextValues });
      setFormInitialValues((prev) => ({
        ...prev,
        theme_primary_color: normalizedPrimary,
        theme_secondary_color: normalizedSecondary,
      }));
      setThemeDataLoaded(true);
    }
  }, [defaultThemeData, themeDataLoaded, resetForm, formInitialValues, formik.dirty]);

  const [forceLicenseCheck, { error: validateLicenseError, data: licenseData, reset }] = useMutation<LicenseData>(
    buildForceLicenseMutation(true, formik.getFieldProps(LICENSE).value as string),
  );

  useEffect(() => {
    if (formik.dirty) {
      window.onbeforeunload = (): boolean => true;
    } else {
      window.onbeforeunload = null;
    }
  }, [formik.dirty]);

  const checkSSOFields = (): boolean => {
    if (
      formik.getFieldProps(OAUTH_CLIENT_ID).value === '' ||
      formik.getFieldProps(OAUTH_PROVIDER_NAME).value === '' ||
      formik.getFieldProps(OAUTH_CLIENT_SECRET).value === '' ||
      formik.getFieldProps(OIDC_DISCOVERY_URL).value === ''
    ) {
      return true;
    } else {
      return false;
    }
  };

  const checkSaveMode = (): boolean => {
    if (formik.getFieldProps(ENABLE_OAUTH).value === 0) {
      return false;
    } else if (isSSOFieldEmpty === true) {
      return true;
    } else {
      return false;
    }
  };

  const isSSOFieldEmpty = checkSSOFields();
  const isSaveDisabled = checkSaveMode();

  const isPulldownDisabled = (): boolean => {
    if (
      formik.getFieldProps(ALLOW_SELF_REGISTRATION).value === 0 ||
      formik.getFieldProps(ALLOW_SELF_REGISTRATION).value === '0'
    ) {
      return true;
    } else {
      return false;
    }
  };

  const authOClientIdLabel = 'oauth_client_id';
  const authOProviderNameLabel = 'oauth_provider_name';
  const authOClientSecretLabel = 'oauth_client_secret';
  const oidcDiscoveryURLLabel = 'oidc_dicovery_url';

  useEffect(() => {
    if (validateLicenseError) {
      toastr.error(validateLicenseError.message);
      reset();
    }
  }, [validateLicenseError, reset, toastr]);

  useEffect(() => {
    if (licenseData?.forceLicenseCheck?.license?.isValid === false) {
      toastr.error('License is Invalid');
    }
    if (licenseData?.forceLicenseCheck?.license?.isValid === true) {
      toastr.success('License is Valid');
    }
    context.refetchLicense?.();
  }, [licenseData, context, toastr]);

  const onDropLogo = (e: FileObject[], logoKey: string): void => {
    const imageData = `${e[0].data}`.replace(/data:image\/[^;]+;base64,/, '');

    setLogos({
      ...logos,
      [logoKey]: imageData,
    });
  };

  const getLogo = (key: string): string => {
    const logoData = logos[key].replace(/^"|"$/g, '');
    let prefix: string;

    if (logoData.startsWith('iVBORw0KGgo')) {
      prefix = 'data:image/png;base64,';
    } else if (logoData.startsWith('AAABAA')) {
      prefix = 'data:image/vnd.microsoft.icon;base64,';
    } else {
      prefix = 'data:image/jpeg;base64,';
    }
    return prefix + logoData;
  };

  if (viewState.signedInUserRole === 'ADM')
    return (
      <Box sx={{ width: '98%', margin: '20px 20px 30px 20px' }}>
        {/* Note: Navigation blocking handled by window.onbeforeunload set in useEffect above */}
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={GRID_CONTAINER_SPACING}>
            <Grid item xs={6}>
              <Card display="flex">
                <div className={classes.cardTitle}>General</div>
                <div className={classes.adminTextInput}>
                  <AdminTextInput label={'Site Title'} {...formik.getFieldProps(SITE_TITLE)} />
                  <AdminTextInput
                    multiline={true}
                    rows={6}
                    style={{ padding: '7px' }}
                    label={'Login Message'}
                    {...formik.getFieldProps(CUSTOM_LOGIN_MESSAGE)}
                  />
                </div>
                <div className={classes.container}>
                  <AdminTextInput
                    className={classes.licenseField}
                    label={'License'}
                    {...formik.getFieldProps(LICENSE)}
                  />
                </div>

                <Button variant="contained" color="primary" className={classes.btn} onClick={() => forceLicenseCheck()}>
                  Validate
                </Button>
              </Card>
              <Card style={{ marginTop: '20px' }} display="flex">
                <div className={classes.cardTitle}>SMTP</div>
                <div className={classes.adminTextInput}>
                  <AdminTextInput label={'Email from address'} {...formik.getFieldProps(EMAIL_FROM_ADDRESS)} />
                  <AdminTextInput label={'SMTP Host'} {...formik.getFieldProps(SMTP_HOST)} />
                  <AdminTextInput label={'SMTP Port'} {...formik.getFieldProps(SMTP_PORT)} />
                  <AdminTextInput label={'SMTP User'} {...formik.getFieldProps(SMTP_USER)} />
                  <AdminTextInput
                    type="password"
                    placeholder="**********"
                    label={'SMTP Password'}
                    {...formik.getFieldProps(SMTP_PASSWORD)}
                  />
                </div>
              </Card>

              <Card style={{ marginTop: '20px' }} display="flex">
                <div className={classes.cardTitle}>SSO</div>
                <Stack direction="row" spacing={1} alignItems="center" className={classes.switchStack}>
                  <Typography>Enable SSO</Typography>
                  <SSOCustomSwitch
                    inputProps={{ 'aria-label': 'ant design' }}
                    className={classes.switch}
                    value={formik.getFieldProps(ENABLE_OAUTH).value as number}
                    onChangeSwitch={(nextValues) => formik.setValues({ ...formik.values, ...nextValues })}
                  />
                </Stack>
                <div className={classes.adminTextInput}>
                  <AdminTextInput
                    disabled={formik.getFieldProps(ENABLE_OAUTH).value === 0}
                    label={authOClientIdLabel}
                    {...formik.getFieldProps(OAUTH_CLIENT_ID)}
                  />
                  {formik.getFieldProps(OAUTH_CLIENT_ID).value === '' && (
                    <Typography className={classes.requiredText}>* Required</Typography>
                  )}
                  <AdminTextInput
                    disabled={formik.getFieldProps(ENABLE_OAUTH).value === 0}
                    label={authOProviderNameLabel}
                    {...formik.getFieldProps(OAUTH_PROVIDER_NAME)}
                  />
                  {formik.getFieldProps(OAUTH_PROVIDER_NAME).value === '' && (
                    <Typography className={classes.requiredText}>* Required</Typography>
                  )}
                  <AdminTextInput
                    disabled={formik.getFieldProps(ENABLE_OAUTH).value === 0}
                    type="password"
                    placeholder="**********"
                    label={authOClientSecretLabel}
                    {...formik.getFieldProps(OAUTH_CLIENT_SECRET)}
                  />
                  {formik.getFieldProps(OAUTH_CLIENT_SECRET).value === '' && (
                    <Typography className={classes.requiredText}>* Required</Typography>
                  )}
                  <AdminTextInput
                    disabled={formik.getFieldProps(ENABLE_OAUTH).value === 0}
                    label={oidcDiscoveryURLLabel}
                    {...formik.getFieldProps(OIDC_DISCOVERY_URL)}
                  />
                  {formik.getFieldProps(OIDC_DISCOVERY_URL).value === '' && (
                    <Typography className={classes.requiredText}>* Required</Typography>
                  )}
                  <div style={{ display: 'flex' }}>
                    <FormControl className={classes.defaultPermGroup}>
                      <Typography
                        sx={{ color: isPulldownDisabled() ? 'text.disabled' : 'text.primary' }}
                        className={classes.defaultPermText}
                      >
                        Permission Group
                      </Typography>
                      <CustomSelect
                        value={formik.getFieldProps(DEFAULT_SSO_PERMISSIONS_GROUP).value as string}
                        disabled={isPulldownDisabled()}
                        name={DEFAULT_SSO_PERMISSIONS_GROUP}
                        onChange={(e: SelectChangeEvent<string>) =>
                          formik.setFieldValue(DEFAULT_SSO_PERMISSIONS_GROUP, e.target.value)
                        }
                        className={classes.defaultPermSelect}
                      >
                        {groupsList?.allPermissionGroups.edges?.map((mediaGroupName) => (
                          <MenuItem key={mediaGroupName?.node?.name} value={mediaGroupName?.node?.name}>
                            {mediaGroupName?.node?.name}
                          </MenuItem>
                        ))}
                      </CustomSelect>
                    </FormControl>

                    <div style={{ display: 'flex', marginTop: '10px', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values[DEFAULT_SSO_PERMISSIONS_EDIT] === '1'}
                            onChange={(e) => {
                              formik.setFieldValue(DEFAULT_SSO_PERMISSIONS_EDIT, e.target.checked ? '1' : '0');
                            }}
                            color="primary"
                          />
                        }
                        label="Edit"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values[DEFAULT_SSO_PERMISSIONS_SHARE] === '1'}
                            onChange={(e) => {
                              formik.setFieldValue(DEFAULT_SSO_PERMISSIONS_SHARE, e.target.checked ? '1' : '0');
                            }}
                            color="primary"
                          />
                        }
                        label="Share"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values[DEFAULT_SSO_PERMISSIONS_DOWNLOAD] === '1'}
                            onChange={(e) => {
                              formik.setFieldValue(DEFAULT_SSO_PERMISSIONS_DOWNLOAD, e.target.checked ? '1' : '0');
                            }}
                            color="primary"
                          />
                        }
                        label="Download"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values[DEFAULT_SSO_PERMISSIONS_DELETE] === '1'}
                            onChange={(e) => {
                              formik.setFieldValue(DEFAULT_SSO_PERMISSIONS_DELETE, e.target.checked ? '1' : '0');
                            }}
                            color="primary"
                          />
                        }
                        label="Delete"
                      />
                    </div>
                  </div>
                </div>
              </Card>
            </Grid>

            <Grid className={classes.themeAndLogosGrid} item xs={6}>
              <Card display="flex">
                <div className={classes.cardTitle}>Options</div>
                <div className={classes.switchGroup}>
                  <Stack direction="row" spacing={1} alignItems="center" className={classes.switchStack}>
                    <Typography>Download Subclips</Typography>
                    <SubclipsCustomSwitch
                      inputProps={{ 'aria-label': 'ant design' }}
                      className={classes.switch}
                      value={formik.getFieldProps(DOWNLOAD_MARKERS).value as number}
                      onChangeSwitch={(nextValues) => formik.setValues({ ...formik.values, ...nextValues })}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" className={classes.switchStack}>
                    <Typography>Enable Request Hires</Typography>
                    <HiresCustomSwitch
                      inputProps={{ 'aria-label': 'ant design' }}
                      className={classes.switch}
                      value={formik.getFieldProps(DOWNLOAD_HIRES).value as number}
                      onChangeSwitch={(nextValues) => formik.setValues({ ...formik.values, ...nextValues })}
                    />
                  </Stack>
                </div>
              </Card>
              <Card className={classes.themeSection} display="flex">
                <div className={classes.cardTitle}>Theme</div>
                <ThemeColorGroup
                  label="Primary"
                  name={THEME_PRIMARY_COLOR}
                  value={(formik.values[THEME_PRIMARY_COLOR] as string) ?? ''}
                  onChange={(json) => formik.setFieldValue(THEME_PRIMARY_COLOR, json)}
                />
                <ThemeColorGroup
                  label="Secondary"
                  name={THEME_SECONDARY_COLOR}
                  value={(formik.values[THEME_SECONDARY_COLOR] as string) ?? ''}
                  onChange={(json) => formik.setFieldValue(THEME_SECONDARY_COLOR, json)}
                />
              </Card>
              <Card className={classes.logosSection} display="flex">
                <div className={classes.dropAndImage}>
                  <div className={classes.textContainer}>
                    <div className={classes.bulletLogin1}>
                      <Typography paragraph className={classes.logoTextsLogin1}>
                        Landing Page Logo
                      </Typography>
                    </div>
                    <div className={classes.bulletLogin2}>
                      <CircleIcon style={{ width: '9px' }} className={classes.circleIcon} />
                      <Typography className={classes.logoTextsLogin2}>Dimensions: Up to 600x600px</Typography>
                    </div>
                    <div className={classes.bulletLogin3}>
                      <CircleIcon style={{ width: '9px' }} className={classes.circleIcon} />
                      <Typography className={classes.logoTextsLogin3}>File Type: .jpg, .svg, .png</Typography>
                    </div>
                  </div>
                  <DropzoneAreaBase
                    fileObjects={[]}
                    dropzoneClass={classes.dropzone}
                    acceptedFiles={['image/png', 'image/jpeg']}
                    onAdd={(e) => onDropLogo(e, LOGIN_LOGO_OPTION_KEY)}
                  />
                  {logos[LOGIN_LOGO_OPTION_KEY] && (
                    <img className={classes.adminLoginLogo} src={getLogo(LOGIN_LOGO_OPTION_KEY)} alt="Login Logo" />
                  )}
                </div>
                <div className={classes.dropAndImage}>
                  <div className={classes.textContainer}>
                    <div className={classes.bulletHeader1}>
                      <Typography className={classes.logoTextsHeader1}>Header Logo</Typography>
                    </div>
                    <div className={classes.bulletHeader2}>
                      <CircleIcon style={{ width: '9px' }} className={classes.circleIcon} />
                      <Typography className={classes.logoTextsHeader2}>Dimensions: Up to 160x40px</Typography>
                    </div>
                    <div className={classes.bulletHeader3}>
                      <CircleIcon style={{ width: '9px' }} className={classes.circleIcon} />
                      <Typography className={classes.logoTextsHeader3}>File Type: .jpg, .svg, .png</Typography>
                    </div>
                  </div>
                  <DropzoneAreaBase
                    fileObjects={[]}
                    dropzoneClass={classes.dropzone}
                    acceptedFiles={['image/png', 'image/jpeg']}
                    onAdd={(e) => onDropLogo(e, HEADER_LOGO_OPTION_KEY)}
                  />
                  {logos[HEADER_LOGO_OPTION_KEY] && (
                    <img className={classes.adminHeaderLogo} src={getLogo(HEADER_LOGO_OPTION_KEY)} alt="Header Logo" />
                  )}
                </div>
                <div className={classes.dropAndImage}>
                  <div className={classes.textContainer}>
                    <div className={classes.bulletFavicon1}>
                      <Typography className={classes.logoTextsFavicon1}>Favicon Logo</Typography>
                    </div>
                    <div className={classes.bulletFavicon2}>
                      <CircleIcon style={{ width: '9px' }} className={classes.circleIcon} />
                      <Typography className={classes.logoTextsFavicon2}>Dimensions: 64x64px</Typography>
                    </div>
                    <div className={classes.bulletFavicon3}>
                      <CircleIcon style={{ width: '9px' }} className={classes.circleIcon} />
                      <Typography className={classes.logoTextsFavicon3}>File Type: .ico, .png, .jpg</Typography>
                    </div>
                  </div>
                  <DropzoneAreaBase
                    fileObjects={[]}
                    dropzoneClass={classes.dropzone}
                    acceptedFiles={['image/vnd.microsoft.icon', 'image/x-icon', 'image/png', 'image/jpeg']}
                    onAdd={(e) => onDropLogo(e, FAV_ICON_OPTION_KEY)}
                  />
                  {logos[FAV_ICON_OPTION_KEY] && (
                    <img className={classes.adminFaviconLogo} src={getLogo(FAV_ICON_OPTION_KEY)} alt="Favicon" />
                  )}
                </div>
              </Card>
              <Card style={{ marginTop: '20px' }} display="flex">
                <div className={classes.cardTitle}>Self Registration</div>
                <Stack direction="row" spacing={1} alignItems="center" className={classes.switchStack}>
                  <Typography>Enable Self Registration</Typography>
                  <SelfRegistrationCustomSwitch
                    inputProps={{ 'aria-label': 'ant design' }}
                    className={classes.switch}
                    value={formik.getFieldProps(ALLOW_SELF_REGISTRATION).value as number}
                    onChangeSwitch={(nextValues) => formik.setValues({ ...formik.values, ...nextValues })}
                  />
                </Stack>
                <FormControl className={classes.defaultPermGroup}>
                  <Typography
                    sx={{ color: isPulldownDisabled() ? 'text.disabled' : 'text.primary' }}
                    className={classes.defaultPermText}
                  >
                    Permission Group
                  </Typography>
                  <CustomSelect
                    value={formik.getFieldProps(DEFAULT_PERMISSIONS_GROUP).value as string}
                    disabled={isPulldownDisabled()}
                    name={DEFAULT_PERMISSIONS_GROUP}
                    onChange={(e: SelectChangeEvent<string>) =>
                      formik.setFieldValue(DEFAULT_PERMISSIONS_GROUP, e.target.value)
                    }
                    className={classes.defaultPermSelect}
                  >
                    {groupsList?.allPermissionGroups.edges?.map((mediaGroupName) => (
                      <MenuItem key={mediaGroupName?.node?.name} value={mediaGroupName?.node?.name}>
                        {mediaGroupName?.node?.name}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </FormControl>
              </Card>
              <ReindexCard reindex={reindex} classes={classes} />
            </Grid>
          </Grid>
          <div className={classes.systemSettingsButtons}>
            <Button
              disabled={isSaveDisabled}
              className={classes.saveButton}
              variant="contained"
              color="primary"
              type="submit"
            >
              Save
            </Button>
            <Button onClick={() => formik.resetForm()} color="primary">
              Reset
            </Button>
          </div>
        </form>
      </Box>
    );
  else if (viewState.signedInUserRole === 'MGR' || viewState.signedInUserRole === 'USR') {
    return (
      <div className={classes.accessDeny}>
        <Typography className={classes.accessDenyText}>You do not have permission to access system settings</Typography>
        <Button variant="contained" color="primary" className={classes.goHomeButton} onClick={goHomeHandler}>
          Back to Library Page
        </Button>
      </div>
    );
  }

  return null;
}

const SSOCustomSwitch = ({ onChangeSwitch, value, ...rest }: CustomSwitchProps): React.ReactElement => {
  const onSSOChangeVal = (e: ChangeEvent<HTMLInputElement>): void => {
    onChangeSwitch({ [ENABLE_OAUTH]: e.target.checked ? 1 : 0 });
  };
  const isChecked = String(value) === '1';
  return <Switch {...rest} onChange={onSSOChangeVal} checked={isChecked} />;
};

const SubclipsCustomSwitch = ({ onChangeSwitch, value, ...rest }: CustomSwitchProps): React.ReactElement => {
  const onSubclipsChangeVal = (e: ChangeEvent<HTMLInputElement>): void => {
    onChangeSwitch({ [DOWNLOAD_MARKERS]: e.target.checked ? 1 : 0 });
  };
  const isChecked = String(value) === '1';
  return <Switch {...rest} onChange={onSubclipsChangeVal} checked={isChecked} />;
};

const HiresCustomSwitch = ({ onChangeSwitch, value, ...rest }: CustomSwitchProps): React.ReactElement => {
  const onHiresChangeVal = (e: ChangeEvent<HTMLInputElement>): void => {
    onChangeSwitch({ [DOWNLOAD_HIRES]: e.target.checked ? 1 : 0 });
  };
  const isChecked = String(value) === '1';
  return <Switch {...rest} onChange={onHiresChangeVal} checked={isChecked} />;
};

const SelfRegistrationCustomSwitch = ({ onChangeSwitch, value, ...rest }: CustomSwitchProps): React.ReactElement => {
  const onSelfRegistrationChangeVal = (e: ChangeEvent<HTMLInputElement>): void => {
    onChangeSwitch({ [ALLOW_SELF_REGISTRATION]: e.target.checked ? 1 : 0 });
  };
  const isChecked = String(value) === '1';
  return <Switch {...rest} onChange={onSelfRegistrationChangeVal} checked={isChecked} />;
};

interface CustomSelectProps {
  value: string;
  disabled: boolean;
  name: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  className: string;
  children: React.ReactNode;
}

const CustomSelect = ({ value, ...rest }: CustomSelectProps): React.ReactElement => {
  return <Select {...rest} value={value} />;
};
